import { useEffect, useRef, useState } from 'react'

import { ecrireReglages } from '../../db/repo'
import { dateDernierExport, exporter, importer, nomFichierExport, poidsPiecesEncodees } from '../../db/sauvegarde'
import { formatJour } from '../../domain/temps'
import type { ModeEstimation } from '../../domain/types'
import { lireQuota } from '../../pwa/register'
import { Carte, Champ, Confirmation, Encart, Entete, SelectCompte, remettreFichier, useAction, useAsync } from '../composants'
import { useDonnees } from '../donnees'

const Mo = (o: number) => `${(o / 1024 / 1024).toFixed(o < 10 * 1024 * 1024 ? 1 : 0)} Mo`

export function PageReglages() {
  const { d, reglages } = useDonnees()
  const [nom, setNom] = useState(reglages.emetteurNom)
  const [adresse, setAdresse] = useState(reglages.emetteurAdresse)
  const [iban, setIban] = useState(reglages.emetteurIban)
  const [enregistre, setEnregistre] = useState(false)

  const quota = useAsync(lireQuota, [])
  const poids = useAsync(poidsPiecesEncodees, [])
  const dernierExport = useAsync(dateDernierExport, [])

  const { erreur, enCours, executer } = useAction()
  const [message, setMessage] = useState<string | null>(null)
  const [aImporter, setAImporter] = useState<{ contenu: unknown; nom: string } | null>(null)
  const entree = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!enregistre) return
    const t = setTimeout(() => setEnregistre(false), 2000)
    return () => clearTimeout(t)
  }, [enregistre])

  const exporterFichier = (complet: boolean) =>
    executer(async () => {
      setMessage(null)
      const s = await exporter(complet)
      const blob = new Blob([JSON.stringify(s)], { type: 'application/json' })
      const issue = await remettreFichier(blob, nomFichierExport(complet))
      if (issue !== 'annule') setMessage(`Sauvegarde exportée : ${d.operations.length} opérations${complet ? `, ${s.pieces.length} pièces jointes` : ''}.`)
      dernierExport.recharger()
    })

  return (
    <>
      <Entete titre="Réglages" retour />
      <main className="contenu">
        <Carte titre="Vos coordonnées">
          <div className="pile">
            <p className="tres-doux">Imprimées en en-tête des justificatifs.</p>
            <Champ libelle="Nom">
              <input type="text" value={nom} onChange={(e) => setNom(e.target.value)} />
            </Champ>
            <Champ libelle="Adresse">
              <textarea rows={3} value={adresse} onChange={(e) => setAdresse(e.target.value)} placeholder={'Rue du Midi 3\n1003 Lausanne'} />
            </Champ>
            <Champ libelle="IBAN pour les versements">
              <input type="text" value={iban} autoCapitalize="characters" placeholder="CH00 0000 0000 0000 0000 0" onChange={(e) => setIban(e.target.value.toUpperCase())} />
            </Champ>
            <button
              type="button"
              className="btn btn--principal"
              onClick={() => void ecrireReglages({ emetteurNom: nom.trim(), emetteurAdresse: adresse.trim(), emetteurIban: iban.trim() }).then(() => setEnregistre(true))}
            >
              {enregistre ? 'Enregistré' : 'Enregistrer'}
            </button>
          </div>
        </Carte>

        <Carte titre="Saisie">
          <Champ libelle="Compte proposé par défaut">
            <SelectCompte comptes={d.comptes} valeur={reglages.compteParDefautId} tous="Le premier compte" onChange={(c) => void ecrireReglages({ compteParDefautId: c })} />
          </Champ>
        </Carte>

        <Carte titre="Prévisionnel">
          <div className="pile">
            <Champ libelle="Estimation des dépenses variables">
              <select value={reglages.modeEstimation} onChange={(e) => void ecrireReglages({ modeEstimation: e.target.value as ModeEstimation })}>
                <option value="auto">Automatique (saisonnalité détectée)</option>
                <option value="moyenne">Moyenne des derniers mois</option>
                <option value="annee-precedente">Même mois l’an dernier</option>
              </select>
            </Champ>
            <Champ libelle="Mois pris en compte dans la moyenne">
              <input type="number" min={1} max={12} value={reglages.moisMoyenne} onChange={(e) => void ecrireReglages({ moisMoyenne: Math.min(12, Math.max(1, Number(e.target.value) || 3)) })} />
            </Champ>
            {reglages.modeEstimation === 'auto' && (
              <Champ
                libelle="Seuil de saisonnalité"
                aide="Dispersion des 12 derniers mois au-delà de laquelle une catégorie est jugée saisonnière (chauffage, vacances) et estimée d’après le même mois l’an dernier. Plus bas : bascule plus souvent."
              >
                <input type="number" min={0.1} max={3} step={0.1} value={reglages.seuilSaisonnalite} onChange={(e) => void ecrireReglages({ seuilSaisonnalite: Number(e.target.value) || 0.6 })} />
              </Champ>
            )}
          </div>
        </Carte>

        <Carte titre="Sauvegarde">
          <div className="pile">
            <Encart ton="alerte">
              Vos données ne sont stockées que sur cet appareil. Aucun serveur n’en garde de copie : exportez régulièrement et rangez le
              fichier en lieu sûr.
            </Encart>
            {dernierExport.valeur && <p className="tres-doux">Dernier export : {formatJour(dernierExport.valeur.slice(0, 10))}</p>}
            <button type="button" className="btn" disabled={enCours} onClick={() => void exporterFichier(false)}>
              Exporter les données
            </button>
            <button type="button" className="btn" disabled={enCours} onClick={() => void exporterFichier(true)}>
              Exporter avec les pièces jointes{poids.valeur ? ` (≈ ${Mo(poids.valeur)})` : ''}
            </button>
            <button type="button" className="btn" disabled={enCours} onClick={() => entree.current?.click()}>
              Restaurer une sauvegarde…
            </button>
            <input
              ref={entree}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0]
                e.target.value = ''
                if (!f) return
                void executer(async () => {
                  setAImporter({ contenu: JSON.parse(await f.text()), nom: f.name })
                })
              }}
            />
            {message && <Encart ton="ok">{message}</Encart>}
            {erreur && <Encart ton="danger">{erreur}</Encart>}
          </div>
        </Carte>

        {quota.valeur && (
          <Carte titre="Stockage">
            <p className="doux">
              {Mo(quota.valeur.utiliseOctets)} utilisés
              {quota.valeur.quotaOctets > 0 && ` sur ${Mo(quota.valeur.quotaOctets)} disponibles`}.{' '}
              {quota.valeur.persistant ? 'Stockage persistant accordé.' : 'Stockage non garanti : installez l’app sur l’écran d’accueil.'}
            </p>
          </Carte>
        )}
      </main>

      {aImporter && (
        <ChoixImport
          nom={aImporter.nom}
          onAnnuler={() => setAImporter(null)}
          onChoisir={(remplacer) => {
            const contenu = aImporter.contenu
            setAImporter(null)
            void executer(async () => {
              const r = await importer(contenu, remplacer)
              setMessage(`Restauration terminée : ${r.operations} opérations, ${r.pieces} pièces jointes.`)
            })
          }}
        />
      )}
    </>
  )
}

function ChoixImport({ nom, onAnnuler, onChoisir }: { nom: string; onAnnuler: () => void; onChoisir: (remplacer: boolean) => void }) {
  const [remplacer, setRemplacer] = useState<boolean | null>(null)
  if (remplacer === null) {
    return (
      <div className="modale-fond" role="presentation" onClick={onAnnuler}>
        <div className="modale" role="dialog" aria-modal="true" aria-label="Restaurer" onClick={(e) => e.stopPropagation()}>
          <div>
            <h2>Restaurer « {nom} »</h2>
            <p className="doux" style={{ marginTop: 6 }}>
              <strong>Remplacer</strong> efface tout ce qui est sur cet appareil (nouveau téléphone). <strong>Fusionner</strong> ajoute la
              sauvegarde aux données actuelles ; en cas de doublon, la sauvegarde l’emporte.
            </p>
          </div>
          <div className="pile">
            <button type="button" className="btn btn--danger" onClick={() => setRemplacer(true)}>Remplacer tout</button>
            <button type="button" className="btn btn--principal" onClick={() => onChoisir(false)}>Fusionner</button>
            <button type="button" className="btn" onClick={onAnnuler}>Annuler</button>
          </div>
        </div>
      </div>
    )
  }
  return (
    <Confirmation
      titre="Tout remplacer ?"
      message="Toutes les opérations, récurrences et pièces jointes actuellement sur cet appareil seront définitivement effacées."
      libelleConfirmer="Effacer et restaurer"
      danger
      onAnnuler={onAnnuler}
      onConfirmer={() => onChoisir(true)}
    />
  )
}
